import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DisplayUserEmail, GalleryDto, GalleryMemberDto, TenantDto } from '@nusign/picflow-backend-types';
import { catchError, combineLatest, filter, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { EmailsEntryFieldComponent } from '../../../account-gallery-shared/components/emails-entry-field/emails-entry-field.component';
import { AutocompleteResultsComponent } from '../../../account/components/autocomplete-results/autocomplete-results.component';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { DialogService } from '../../../services/dialog.service';
import { AutoCompleteItem, EmailAutocompleteService } from '../../../services/email-autocomplete.service';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { GalleryService } from '../../../services/gallery.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { UserGroupService } from '../../../services/user-group.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { ShareGalleryDialogComponent } from '../../dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { GalleryInviteAccessItemComponent } from '../gallery-invite-access-item/gallery-invite-access-item.component';
import { GalleryMemberAccessItemComponent } from '../gallery-member-access-item/gallery-member-access-item.component';
import { GalleryOwnerAccessItemComponent } from '../gallery-owner-access-item/gallery-owner-access-item.component';
import { GalleryPublicAccessItemComponent } from '../gallery-public-access-item/gallery-public-access-item.component';
import { GalleryTeamAccessItemComponent } from '../gallery-team-access-item/gallery-team-access-item.component';
import { GalleryUserGroupAccessItemComponent } from '../gallery-user-group-access-item/gallery-user-group-access-item.component';

@Component({
  selector: 'app-share-gallery-collaborators',
  imports: [
    SvgComponent,
    ButtonDirective,
    EmailsEntryFieldComponent,
    GalleryShareContextComponent,
    AutocompleteResultsComponent,
    SwitchComponent,
    InputFieldComponent,
    ClickTrackDirective,
    FormsModule,
    ReactiveFormsModule,
    LoadingIndicatorComponent,
    GalleryPublicAccessItemComponent,
    GalleryTeamAccessItemComponent,
    BillingScopePipe,
    GalleryUserGroupAccessItemComponent,
    GalleryMemberAccessItemComponent,
    GalleryOwnerAccessItemComponent,
    GalleryInviteAccessItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './share-gallery-collaborators.component.html',
  styleUrl: './share-gallery-collaborators.component.scss',
})
export class ShareGalleryCollaboratorsComponent {
  private galleryMemberService = inject(GalleryMemberService);
  private galleryService = inject(GalleryService);
  private userGroupService = inject(UserGroupService);
  private emailAutocompleteService = inject(EmailAutocompleteService);
  private dialog = inject(DialogService);
  private toasts = inject(ToastService);
  private state = inject(StateService);
  editLink = output<void>();
  gallery = input.required<GalleryDto>();

  public galleryPermissions = toSignal(
    toObservable(this.gallery).pipe(switchMap(gallery => this.galleryService.getGalleryPermissions(gallery)))
  );

  mainGallery = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => {
        if (gallery.parent_gallery) return this.galleryService.getGallery(gallery.parent_gallery as string);
        return of(gallery);
      })
    )
  );

  isSubGallery = computed(() => {
    return !!this.gallery()?.parent_gallery;
  });

  galleryUserGroups = toSignal(
    combineLatest([this.userGroupService.getUserGroups(), toObservable(this.mainGallery)]).pipe(
      switchMap(([userGroups, mainGallery]) =>
        this.userGroupService.getGalleryUserGroups(mainGallery.id).pipe(
          map(galleryGroups => {
            return userGroups
              .map(group => {
                const galleryGroup = galleryGroups.find(g => g.group === group.id);
                if (!galleryGroup) return;
                return {
                  group: group,
                  accessLevel: galleryGroup.access,
                };
              })
              .filter(Boolean);
          })
        )
      ),
      shareReplay(1)
    )
  );

  autoCompleteIndex = signal(0);
  inviteMode = signal(false);
  sendingInvites = signal(false);
  emailString = signal('');
  inviteAccessLevel = signal<GalleryAccessLevel>('edit');
  inviteAccessLevels: GalleryAccessLevel[] = ['edit', 'review', 'view_only'];
  emailsEntryField = viewChild<EmailsEntryFieldComponent>('emailsEntryField');
  inviteModeChanged = output<boolean>();
  showAutocompleteResults = signal(false);
  allAutocompleteResults = toSignal(
    toObservable(this.showAutocompleteResults)
      .pipe(filter(Boolean))
      .pipe(switchMap(() => this.emailAutocompleteService.results$))
  );
  autocompleteResults = computed(() => {
    return this.emailAutocompleteService.filterResults(
      this.emailString(),
      this.allAutocompleteResults(),
      this.emailsEntryField()
        ?.emails()
        .map(e => e.toLowerCase()),
      this.galleryMembers(),
      this.galleryInvites(),
      this.owner()
    );
  });

  messageForm = new FormGroup({
    notify: new FormControl(true),
    message: new FormControl(''),
  });

  galleryMembers = toSignal(
    toObservable(this.mainGallery).pipe(
      filter(Boolean),
      switchMap(
        mainGallery =>
          this.galleryMemberService.getGalleryMembers(mainGallery.id) as Observable<
            (GalleryMemberDto & { user: DisplayUserEmail })[]
          >
      )
    )
  );

  isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  galleryInvites = toSignal(
    combineLatest([
      toObservable(this.mainGallery).pipe(filter(Boolean)),
      toObservable(this.galleryPermissions).pipe(filter(permissions => permissions?.can_edit)),
      toObservable(this.isTeamMember).pipe(filter(Boolean)),
    ]).pipe(switchMap(([mainGallery]) => this.galleryMemberService.getPendingGalleryMembers(mainGallery.id)))
  );

  owner = toSignal(
    combineLatest([toObservable(this.gallery), toObservable(this.isTeamMember)]).pipe(
      filter(([gallery]) => !!gallery),
      switchMap(([gallery, isTeamMember]) => this.galleryService.getGalleryOwner(gallery.id, isTeamMember))
    )
  );

  loading = computed(() => {
    const galleryMembers = this.galleryMembers();
    const galleryInvites = this.galleryInvites();
    const isTeamMember = this.isTeamMember();
    const permissions = this.galleryPermissions();
    const gallery = this.gallery();
    const owner = this.owner();
    if (!gallery || !permissions || owner === undefined) return true;
    if (!isTeamMember || !permissions.can_edit) return false;
    return !galleryMembers || !galleryInvites;
  });

  constructor() {
    effect(() => {
      this.inviteModeChanged.emit(this.inviteMode());
    });
  }

  onBlur() {
    setTimeout(() => {
      this.showAutocompleteResults.set(false);
    }, 1);
  }

  onFocus() {
    this.showAutocompleteResults.set(true);
  }

  onEmailChange(event: [boolean, string]) {
    this.emailString.set(event[1]);
  }

  selectAutocompleteResult(result: AutoCompleteItem) {
    this.emailsEntryField().form.setValue({ emails: result.email });
    this.emailsEntryField().blur();
  }

  autocompleteMethod() {
    return (email: string) => this.autocompleteResults()[this.autoCompleteIndex()]?.email || email;
  }

  validateEmail(): (email: string) => boolean {
    return (email: string) => {
      return !this.getEmailErrorMessage()(email);
    };
  }

  getEmailErrorMessage(): (email: string) => string {
    return (email: string) => {
      return this.emailAutocompleteService.getErrorMessage(
        email,
        this.galleryMembers(),
        this.galleryInvites(),
        this.owner()
      );
    };
  }

  sendInvites() {
    if (this.sendingInvites()) return;
    if (!this.emailsEntryField().getEmails().length) {
      return this.emailsEntryField().parseEmails();
    }
    const inviteEmails = this.emailsEntryField().getEmails();
    if (!inviteEmails.length) return;

    this.sendingInvites.set(true);
    forkJoinSafe([
      ...inviteEmails.map(email => {
        const notify = this.messageForm.value.notify;

        return this.galleryMemberService.inviteMember(
          email,
          this.mainGallery().id,
          this.inviteAccessLevel(),
          notify,
          (this.messageForm.value.message || '').split('\n').join('<br>')
        );
      }),
    ])
      .pipe(
        tap(() => {
          this.emailsEntryField().form.setValue({ emails: '' });
          this.emailsEntryField().emails.set([]);
          this.messageForm.setValue({
            message: '',
            notify: true,
          });
          this.inviteMode.set(false);
          this.toasts.add('Invitations sent!', 'success');
        }),
        catchError(err => {
          if (err === 'waf-challenge') {
            // security challenge, need to warn the user that the email wasn't sent
            this.toasts.add(
              'We were unable to send the invitation. Please try again later or contact support.',
              'error'
            );
            return of(undefined);
          }
          const code = err?.error?.code;
          if (!code) return throwError(() => err);
          if (code === 'too_many_requests') {
            this.toasts.add('Invitation limit reached', 'error');
          }
          return of(undefined);
        }),
        tap(() => {
          this.sendingInvites.set(false);
        })
      )
      .subscribe();
  }

  openMainGalleryShareDialog() {
    this.dialog.open(ShareGalleryDialogComponent, { gallery: this.mainGallery() });
  }
}
